import {
    Grid,
    TableRow,
    Typography
} from '@mui/material';
import { SelectOption, WithdrawalBeneficiaryDetailTypeEnum } from '@tassat/common-definitions';
import { StyledSelect } from '@tassat/formik-input';
import { WithdrawalTypeEnum } from '@tassat/fred-definitions';
import { getWithdrawalTypeDisplayValue } from '@tassat/fred-extended-definitions';
import React from 'react';
import { SingleValue } from 'react-select';

import BeneficiaryAdditionalInfoDetails from './beneficiaryAdditionalInfoDetails';
import BeneficiaryEntitiyDetails from './beneficiaryEntityDetails';
import { renderFields } from './helper';
import { muiStyles } from './styles';
import { WithdrawalInstructionFields, WithdrawalInstructionsDetailsProps, beneficiaryDetailsOptions } from './types';

const withdrawalInstructionFields: WithdrawalInstructionFields = [
    {
        label: 'Withdrawal Description',
        valueProp: 'Description'
    },
    {
        label: 'Withdrawal Type',
        valueProp: 'WithdrawalType',
        valueGetter: (val) => {
            if (typeof val === 'number') {
                return getWithdrawalTypeDisplayValue(String.fromCharCode(val as unknown as number));
            }

            return val ?? '';
        }
    }
];

export const WithdrawalInstructionDetails: React.FC<WithdrawalInstructionsDetailsProps> = (props) => {
    const {
        displayComponent,
        withdrawalInstruction
    } = props;
    const classes = muiStyles(props);

    const isBlockchainWithdrawal = String.fromCharCode(withdrawalInstruction.WithdrawalType as unknown as number) ===
        WithdrawalTypeEnum.BLOCKCHAIN;

    const [ detailsShown, setDetailsShown ] = React.useState<SelectOption | null>(beneficiaryDetailsOptions[0]);
    const selectDetails = (value: SingleValue<SelectOption>) => setDetailsShown(value);

    const renderInformation = (details: SelectOption | null) => {
        if (
            details?.value === WithdrawalBeneficiaryDetailTypeEnum.BENEFICIARY ||
            details?.value === WithdrawalBeneficiaryDetailTypeEnum.BENEFICIARY_BANK ||
            details?.value === WithdrawalBeneficiaryDetailTypeEnum.INTERMEDIARY_BANK
        ) {
            return (
                <BeneficiaryEntitiyDetails
                    displayComponent={displayComponent}
                    entityType={details.value}
                    withdrawalInstruction={withdrawalInstruction}
                />
            );
        }

        if (details?.value === WithdrawalBeneficiaryDetailTypeEnum.ADDITIONAL_INFO) {
            return (
                <BeneficiaryAdditionalInfoDetails
                    displayComponent={displayComponent}
                    withdrawalInstruction={withdrawalInstruction}
                />
            );
        }

        return null;
    };

    const blockchainDetails = (
        <BeneficiaryEntitiyDetails
            displayComponent={displayComponent}
            entityType={WithdrawalBeneficiaryDetailTypeEnum.BLOCKCHAIN}
            withdrawalInstruction={withdrawalInstruction}
        />
    );

    if (displayComponent === 'TransactionDetail') {
        return (
            <Grid
                container
                direction='column'
                spacing={0}
            >
                {renderFields(withdrawalInstruction, withdrawalInstructionFields, classes, displayComponent)}
                {isBlockchainWithdrawal ? blockchainDetails : (
                    <>
                        <StyledSelect
                            className={classes.select}
                            id='transactionDetailSelect'
                            menuPosition='fixed'
                            onChange={selectDetails}
                            options={beneficiaryDetailsOptions}
                            value={detailsShown}
                        />
                        {renderInformation(detailsShown)}
                    </>
                )}
            </Grid>
        );
    }

    if (displayComponent === 'Table') {
        return (
            <>
                {renderFields(withdrawalInstruction, withdrawalInstructionFields, classes, displayComponent)}
                {isBlockchainWithdrawal ? blockchainDetails : (
                    <>
                        <TableRow className={classes.tableRow}>
                            <StyledSelect
                                id='beneficiaryDetailSelect'
                                onChange={selectDetails}
                                options={beneficiaryDetailsOptions}
                                value={detailsShown}
                            />
                        </TableRow>
                        {renderInformation(detailsShown)}
                    </>
                )}
            </>
        );
    }

    return (
        <Grid
            alignItems='center'
            className={classes.root}
            container
            direction='column'
            spacing={0}
        >
            {renderFields(withdrawalInstruction, withdrawalInstructionFields, classes, displayComponent)}
            {isBlockchainWithdrawal ? blockchainDetails : (
                <>
                    <Grid
                        className={classes.selectContainer}
                        container
                        direction='column'
                    >
                        <Grid item>
                            <Typography variant='body2'>Select Information to Display</Typography>
                        </Grid>
                        <Grid
                            item
                            xs={6}
                        >
                            <StyledSelect
                                id='beneficiaryDetailSelect'
                                onChange={selectDetails}
                                options={beneficiaryDetailsOptions}
                                value={detailsShown}
                            />
                        </Grid>
                    </Grid>
                    {renderInformation(detailsShown)}
                </>
            )}
        </Grid>
    );
};